import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../utils/apiClient';
import { readReviewCount, updateReviewCount, validReviewCount } from './reviewCount';

export const useReviewCount = (enabled, context, body, onMessageEdit) => {
  const [state, setState] = useState({ draft: '', saved: null, ready: false, saving: false, error: '', didSave: false });
  const latest = useRef({});
  const generation = useRef(0);
  latest.current = { state, body, onMessageEdit, enabled };
  const valid = validReviewCount(state.draft);
  const bodyCount = readReviewCount(body);
  const matches = bodyCount !== null && bodyCount === Number(state.draft);
  const dirty = state.ready && Number(state.draft) !== state.saved;

  useEffect(() => {
    generation.current += 1;
    const initial = enabled ? readReviewCount(latest.current.body) : null;
    setState({ draft: initial === null ? '' : String(initial), saved: initial, ready: enabled, saving: false, error: '', didSave: false });
    return () => { generation.current += 1; };
  }, [enabled, context]);

  const persist = useCallback(async () => {
    const current = latest.current;
    const target = Number(current.state.draft);
    if (!current.enabled || current.state.saving || !validReviewCount(current.state.draft) || readReviewCount(current.body) !== target || target === current.state.saved) return;
    const version = generation.current;
    latest.current = { ...current, state: { ...current.state, saving: true } };
    setState(previous => ({ ...previous, saving: true, error: '' }));
    try {
      const { data } = await apiClient.put('/settings/review-email', { review_count: target }, { timeout: 10000 });
      if (generation.current === version) setState(previous => ({ ...previous, saved: data.review_count, didSave: Number(previous.draft) === target }));
    } catch (error) {
      if (generation.current === version && Number(latest.current.state.draft) === target) {
        setState(previous => ({ ...previous, error: 'Review count wasn’t saved. Please retry.' }));
      }
    } finally {
      if (generation.current === version) setState(previous => ({ ...previous, saving: false }));
    }
  }, []);

  useEffect(() => {
    if (!enabled || !state.ready || !dirty || !valid || !matches || state.saving || state.error) return;
    const timer = setTimeout(persist, 500);
    return () => clearTimeout(timer);
  }, [enabled, state.ready, state.draft, state.saving, state.error, dirty, valid, matches, persist]);

  const change = value => {
    setState(previous => ({ ...previous, draft: value, error: '', didSave: false }));
    if (validReviewCount(value)) latest.current.onMessageEdit(previousBody => updateReviewCount(previousBody, value));
  };
  const error = state.error || (state.ready && !valid ? 'Enter a whole number from 0 to 9,999,999.' : '') ||
    (state.ready && valid && !matches ? 'The review count is missing or changed in the HTML. Update it with this field.' : '');
  const busy = enabled && (state.saving || (dirty && valid && matches && !error));
  return { ...state, error, busy, change, retry: persist, canRetry: !!state.error,
    blocked: enabled && (!state.ready || !valid || !matches || dirty || state.saving || !!error) };
};